import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router';
import { useShallow } from 'zustand/react/shallow';
import { CalendarClock, MapPin, Pencil, Play, Plus, Radio, Square, Star, Trash2, Users, Volume2, X } from 'lucide-react';
import { EVENT_LIMITS, Permission, type Channel, type ScheduledEvent } from '@spawnwire/shared';
import { Button, clsx, EmptyState, Field, Input, Modal, Select, Spinner, TextArea } from '../../components/ui';
import { errorMessage, http } from '../../lib/api';
import { useNow } from '../../lib/hooks';
import { can, useSpacePermissions } from '../../lib/perms';
import { channelPath } from '../../lib/nav';
import { formatTimestamp, formatTime, sameDay } from '../../lib/time';
import { useStore } from '../../store/store';
import { ui } from '../../store/ui';
import { countdown, emptyForm, formFromEvent, formToCreate, formToPatch, interestText, validateForm, type EventForm } from './logic';
import { events, useEvents } from './store';

/** When an event happens, in the viewer's own time: "Today at 6:00 PM – 8:00 PM". */
function whenText(e: Pick<ScheduledEvent, 'startAt' | 'endAt'>): string {
  const start = formatTimestamp(e.startAt);
  if (!e.endAt) return start;
  return sameDay(e.startAt, e.endAt) ? `${start} – ${formatTime(e.endAt)}` : `${start} – ${formatTimestamp(e.endAt)}`;
}

function EventCard({ event, manage, onEdit }: { event: ScheduledEvent; manage: boolean; onEdit: () => void }) {
  const navigate = useNavigate();
  const now = useNow(30_000);
  const channel = useStore((s) => (event.channelId ? s.channels[event.channelId] : undefined));
  /** What is being sent right now, so only that button shows a spinner and the others wait. */
  const [working, setWorking] = useState<'interest' | 'status' | null>(null);
  const busy = working !== null;
  const [error, setError] = useState<string | null>(null);
  /** An action waiting for a second click, so nothing is ended or deleted by accident. */
  const [confirm, setConfirm] = useState<'end' | 'cancel' | 'delete' | null>(null);
  const live = event.status === 'active';
  const url = `/spaces/${event.spaceId}/events/${event.id}`;

  const run = async (kind: 'interest' | 'status', fn: () => Promise<unknown>) => {
    setWorking(kind);
    setError(null);
    try {
      await fn();
      setConfirm(null);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setWorking(null);
    }
  };
  const toggleInterest = () => run('interest', async () => events.set(await (event.interested ? http.del<ScheduledEvent>(`${url}/interest`) : http.put<ScheduledEvent>(`${url}/interest`))));
  const changeStatus = (status: 'active' | 'completed' | 'canceled') => run('status', async () => events.set(await http.patch<ScheduledEvent>(url, { status })));
  const remove = () =>
    run('status', async () => {
      await http.del(url);
      events.remove(event.spaceId, event.id);
    });

  return (
    <li className="rounded-lg border border-line bg-sidebar p-4" data-testid="event" aria-label={event.name}>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs font-medium">
        {live && (
          <span className="inline-flex items-center gap-1 rounded bg-danger px-1.5 py-0.5 text-[10px] font-bold uppercase text-white">
            <Radio className="size-3" aria-hidden /> Live now
          </span>
        )}
        <span className={clsx(live ? 'text-danger' : 'text-accent')}>{whenText(event)}</span>
        {!live && <span className="text-faint">{countdown(event.startAt, now)}</span>}
      </div>
      <h3 className="mt-1 break-words text-base font-semibold text-fg">{event.name}</h3>
      {event.description && <p className="mt-1 whitespace-pre-wrap break-words text-sm text-muted">{event.description}</p>}
      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted">
        {event.channelId ? (
          <span className="inline-flex items-center gap-1.5">
            <Volume2 className="size-4 shrink-0" aria-hidden />
            {channel ? (
              <button
                type="button"
                className="hover:text-fg hover:underline"
                onClick={() => {
                  ui.close();
                  navigate(channelPath(channel));
                }}
              >
                {channel.name}
              </button>
            ) : (
              'A channel you can’t open'
            )}
          </span>
        ) : (
          <span className="inline-flex min-w-0 items-center gap-1.5">
            <MapPin className="size-4 shrink-0" aria-hidden />
            <span className="break-words">{event.location}</span>
          </span>
        )}
        <span className="inline-flex items-center gap-1.5">
          <Users className="size-4 shrink-0" aria-hidden />
          {interestText(event.interestedCount)}
        </span>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Button size="sm" variant={event.interested ? 'secondary' : 'primary'} icon={Star} loading={working === 'interest'} disabled={busy} aria-pressed={!!event.interested} onClick={() => void toggleInterest()}>
          {event.interested ? 'Interested' : 'Interested?'}
        </Button>
        {manage && confirm === null && (
          <>
            {event.status === 'scheduled' && (
              <Button size="sm" variant="secondary" icon={Play} disabled={busy} onClick={() => void changeStatus('active')}>
                Start now
              </Button>
            )}
            {live && (
              <Button size="sm" variant="secondary" icon={Square} disabled={busy} onClick={() => setConfirm('end')}>
                End event
              </Button>
            )}
            <Button size="sm" variant="ghost" icon={Pencil} disabled={busy} onClick={onEdit}>
              Edit
            </Button>
            {event.status === 'scheduled' && (
              <Button size="sm" variant="ghost" icon={X} disabled={busy} onClick={() => setConfirm('cancel')}>
                Cancel event
              </Button>
            )}
          </>
        )}
        {manage && confirm === null && (
          <Button size="sm" variant="ghost" icon={Trash2} disabled={busy} className="text-danger hover:text-danger" onClick={() => setConfirm('delete')}>
            Delete
          </Button>
        )}
        {confirm && (
          <span className="flex flex-wrap items-center gap-2" role="group" aria-label="Confirm">
            <span className="text-sm text-fg">{confirm === 'end' ? 'End this event now?' : confirm === 'cancel' ? 'Cancel this event?' : 'Delete this event for good?'}</span>
            <Button
              size="sm"
              variant={confirm === 'end' ? 'secondary' : 'danger'}
              loading={busy}
              onClick={() => void (confirm === 'end' ? changeStatus('completed') : confirm === 'cancel' ? changeStatus('canceled') : remove())}
            >
              {confirm === 'end' ? 'Yes, end it' : confirm === 'cancel' ? 'Yes, cancel it' : 'Yes, delete it'}
            </Button>
            <Button size="sm" variant="ghost" disabled={busy} onClick={() => setConfirm(null)}>
              Keep
            </Button>
          </span>
        )}
      </div>
      {error && (
        <p role="alert" className="mt-2 rounded-md bg-danger-soft px-3 py-2 text-sm text-danger">
          {error}
        </p>
      )}
    </li>
  );
}

function EventFormView({ spaceId, event, onDone }: { spaceId: string; event: ScheduledEvent | null; onDone: () => void }) {
  const calls = useStore(useShallow((s) => Object.values(s.channels).filter((c): c is Channel => c.spaceId === spaceId && (c.type === 'voice' || c.type === 'stage')).sort((a, b) => a.position - b.position)));
  const [form, setForm] = useState<EventForm>(() => (event ? formFromEvent(event) : emptyForm(calls[0]?.id ?? '')));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const running = event?.status === 'active';
  const set = (patch: Partial<EventForm>) => setForm((f) => ({ ...f, ...patch }));

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const problem = validateForm(form, Date.now(), running);
    if (problem) return setError(problem);
    setBusy(true);
    setError(null);
    try {
      if (event) {
        const patch = formToPatch(form, event);
        if (patch) events.set(await http.patch<ScheduledEvent>(`/spaces/${spaceId}/events/${event.id}`, patch));
      } else {
        events.set(await http.post<ScheduledEvent>(`/spaces/${spaceId}/events`, formToCreate(form)));
      }
      onDone();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4" aria-label={event ? 'Edit event' : 'Schedule an event'}>
      <Field label="Event name" htmlFor="event-name">
        <Input id="event-name" value={form.name} onChange={(e) => set({ name: e.target.value })} maxLength={EVENT_LIMITS.name} autoFocus placeholder="Game night" />
      </Field>
      <Field label="Where">
        <div className="flex gap-2" role="radiogroup" aria-label="Where it takes place">
          {(
            [
              ['channel', 'Voice channel'],
              ['external', 'Somewhere else'],
            ] as const
          ).map(([kind, label]) => (
            <button
              key={kind}
              type="button"
              role="radio"
              aria-checked={form.kind === kind}
              disabled={running && kind === 'channel' && form.kind === 'external'}
              onClick={() => set({ kind, channelId: kind === 'channel' && !form.channelId ? (calls[0]?.id ?? '') : form.channelId })}
              className={clsx('h-9 flex-1 rounded-md border px-3 text-sm font-medium transition-colors', form.kind === kind ? 'border-accent bg-accent-soft text-fg' : 'border-line text-muted hover:bg-hover')}
            >
              {label}
            </button>
          ))}
        </div>
      </Field>
      {form.kind === 'channel' ? (
        <Field label="Channel" htmlFor="event-channel" hint={calls.length === 0 ? 'This space has no voice or stage channels you can see.' : undefined}>
          <Select
            id="event-channel"
            value={form.channelId}
            onChange={(channelId) => set({ channelId })}
            options={[{ value: '', label: 'Choose a channel…' }, ...calls.map((c) => ({ value: c.id, label: c.name }))]}
          />
        </Field>
      ) : (
        <Field label="Place" htmlFor="event-location" hint="A link, an address, or anywhere people will meet.">
          <Input id="event-location" value={form.location} onChange={(e) => set({ location: e.target.value })} maxLength={EVENT_LIMITS.location} placeholder="The park by the river" />
        </Field>
      )}
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Starts" htmlFor="event-start" hint={running ? 'It has started, so the start can’t change.' : undefined}>
          <Input id="event-start" type="datetime-local" value={form.start} disabled={running} onChange={(e) => set({ start: e.target.value })} />
        </Field>
        <Field label={form.kind === 'external' ? 'Ends' : 'Ends (optional)'} htmlFor="event-end">
          <Input id="event-end" type="datetime-local" value={form.end} onChange={(e) => set({ end: e.target.value })} />
        </Field>
      </div>
      <Field label="Description (optional)" htmlFor="event-description">
        <TextArea id="event-description" rows={3} value={form.description} onChange={(e) => set({ description: e.target.value })} maxLength={EVENT_LIMITS.description} />
      </Field>
      {error && (
        <p role="alert" className="rounded-md bg-danger-soft px-3 py-2 text-sm text-danger">
          {error}
        </p>
      )}
      <div className="flex justify-end gap-2 pt-1">
        <Button variant="ghost" onClick={onDone} disabled={busy}>
          Back
        </Button>
        <Button type="submit" loading={busy}>
          {event ? 'Save changes' : 'Schedule event'}
        </Button>
      </div>
    </form>
  );
}

/** The space's events: what is on, what is coming, what is over; and for people who manage events, making and changing them. */
export function EventsDialog({ spaceId }: { spaceId: string }) {
  const space = useStore((s) => s.spaces[spaceId]);
  const perms = useSpacePermissions(spaceId);
  const manage = can(perms, Permission.MANAGE_EVENTS);
  const list = useEvents((s) => s.bySpace[spaceId]);
  const [tab, setTab] = useState<'upcoming' | 'past'>('upcoming');
  const [past, setPast] = useState<ScheduledEvent[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [editing, setEditing] = useState<ScheduledEvent | 'new' | null>(null);

  useEffect(() => {
    events.load(spaceId).catch((err) => setLoadError(errorMessage(err)));
  }, [spaceId]);

  useEffect(() => {
    if (tab !== 'past') return;
    let stale = false;
    setPast(null);
    http
      .get<{ data: ScheduledEvent[] }>(`/spaces/${spaceId}/events?status=finished&limit=50`)
      .then((res) => !stale && setPast(res.data))
      .catch((err) => !stale && setLoadError(errorMessage(err)));
    return () => {
      stale = true;
    };
  }, [tab, spaceId]);

  const finished = (e: ScheduledEvent) => setPast((p) => p?.filter((x) => x.id !== e.id) ?? p);
  const happening = list?.filter((e) => e.status === 'active') ?? [];
  const coming = list?.filter((e) => e.status === 'scheduled') ?? [];

  return (
    <Modal
      open
      onClose={ui.close}
      size="lg"
      title={editing ? (editing === 'new' ? 'Schedule an event' : 'Edit event') : 'Events'}
      description={editing ? undefined : space ? `Coming up in ${space.name}` : undefined}
    >
      {editing ? (
        <EventFormView spaceId={spaceId} event={editing === 'new' ? null : editing} onDone={() => setEditing(null)} />
      ) : (
        <div className="space-y-4">
          <div className="flex items-center justify-between gap-3">
            <div className="flex gap-1 rounded-md bg-active p-0.5" role="tablist" aria-label="Events">
              {(['upcoming', 'past'] as const).map((t) => (
                <button
                  key={t}
                  type="button"
                  role="tab"
                  aria-selected={tab === t}
                  onClick={() => setTab(t)}
                  className={clsx('h-8 rounded px-3 text-sm font-medium', tab === t ? 'bg-main text-fg shadow-sm' : 'text-muted hover:text-fg')}
                >
                  {t === 'upcoming' ? 'Upcoming' : 'Past'}
                </button>
              ))}
            </div>
            {manage && (
              <Button size="sm" icon={Plus} onClick={() => setEditing('new')}>
                Schedule event
              </Button>
            )}
          </div>
          {loadError && (
            <p role="alert" className="rounded-md bg-danger-soft px-3 py-2 text-sm text-danger">
              {loadError}
            </p>
          )}
          {tab === 'upcoming' ? (
            list === undefined ? (
              <div className="flex justify-center py-10">
                <Spinner />
              </div>
            ) : happening.length + coming.length === 0 ? (
              <EmptyState
                icon={CalendarClock}
                title="No events planned"
                body={manage ? 'Schedule a game night, a stream, a meetup. People can say they’re interested.' : 'When someone schedules an event, it shows up here.'}
              />
            ) : (
              <ul className="space-y-3">
                {[...happening, ...coming].map((e) => (
                  <EventCard key={e.id} event={e} manage={manage} onEdit={() => setEditing(e)} />
                ))}
              </ul>
            )
          ) : past === null ? (
            <div className="flex justify-center py-10">
              <Spinner />
            </div>
          ) : past.length === 0 ? (
            <EmptyState icon={CalendarClock} title="Nothing here yet" body="Events that have finished or been canceled are kept here." />
          ) : (
            <ul className="space-y-3">
              {past.map((e) => (
                <PastCard key={e.id} event={e} manage={manage} onGone={() => finished(e)} />
              ))}
            </ul>
          )}
        </div>
      )}
    </Modal>
  );
}

/** A finished or canceled event: only managers can clean it up. */
function PastCard({ event, manage, onGone }: { event: ScheduledEvent; manage: boolean; onGone: () => void }) {
  const channel = useStore((s) => (event.channelId ? s.channels[event.channelId] : undefined));
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const remove = async () => {
    setBusy(true);
    setError(null);
    try {
      await http.del(`/spaces/${event.spaceId}/events/${event.id}`);
      onGone();
    } catch (err) {
      setError(errorMessage(err));
      setBusy(false);
    }
  };
  return (
    <li className="rounded-lg border border-line bg-sidebar p-4 opacity-90" data-testid="past-event" aria-label={event.name}>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs font-medium text-muted">
        {event.status === 'canceled' && <span className="rounded bg-active px-1.5 py-0.5 text-[10px] font-bold uppercase">Canceled</span>}
        <span>{whenText(event)}</span>
      </div>
      <h3 className="mt-1 break-words text-base font-semibold text-fg">{event.name}</h3>
      <div className="mt-1 text-sm text-muted">{event.location ?? (channel ? channel.name : event.channelId ? 'In a voice channel' : '')}</div>
      {manage && (
        <div className="mt-2 flex items-center gap-2">
          {confirm ? (
            <>
              <span className="text-sm text-fg">Delete this event for good?</span>
              <Button size="sm" variant="danger" loading={busy} onClick={() => void remove()}>
                Yes, delete it
              </Button>
              <Button size="sm" variant="ghost" disabled={busy} onClick={() => setConfirm(false)}>
                Keep
              </Button>
            </>
          ) : (
            <Button size="sm" variant="ghost" icon={Trash2} className="text-danger hover:text-danger" onClick={() => setConfirm(true)}>
              Delete
            </Button>
          )}
        </div>
      )}
      {error && (
        <p role="alert" className="mt-2 rounded-md bg-danger-soft px-3 py-2 text-sm text-danger">
          {error}
        </p>
      )}
    </li>
  );
}
